<script lang="ts">
	import { onMount } from 'svelte';
	import { GetPrimaryGalleryArtworksDocument } from '../../queries/__generated__/getPrimaryGalleryArtworks.generated';
	import { PrimaryGalleryArtworkCard } from './components/PrimaryGalleryArtworkCard';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { LoadMoreInfinite } from '#lib/components/LoadMoreInfinite/index.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { DropType } from '#lib/features/checkout/constants/drop-types.js';
	import { getCartIconContext } from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { PageBody } from '#lib/features/navigation/components/PageBody/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { WishlistButton } from '#lib/features/wishlist/components/WishlistButton/index.js';
	import { getWishlistContext } from '#lib/features/wishlist/utils/getWishlistContext/index.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		getPrimaryCollectionArtworks,
		primaryCollectionArtworksQueryKey,
	} from '#lib/websites/primary/api/getPrimaryCollectionPage.js';
	import { PRIMARY_GALLERY_LIMIT } from '#lib/websites/primary/constants/gallery.js';
	import type { GetPrimaryGalleryArtworksQuery } from '#lib/websites/primary/queries/__generated__/getPrimaryGalleryArtworks.generated.js';
	import { page } from '$app/state';
	import { ArtworkCardLoading } from '$global/components/ArtworkCardLoading';
	import { Breadcrumbs } from '$global/components/Breadcrumbs';
	import { CircularProgress } from '$global/components/CircularProgress';
	import { Container } from '$global/components/Container';
	import { Txt } from '$global/components/Txt';
	import { StickyFilterToolbar } from '$global/features/filter-drawer/components/Filter';
	import { trackSelectItem } from '$global/features/filter-drawer/utils/trackSelectItem/trackSelectItem';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import type { PrimaryGalleryPageData } from '$routes/primary/collection/[collectionSlug]/types';

	// `PrimaryGalleryArtworkCard` and `trackSelectItem` are typed against the
	// GraphQL row, whose `id` is an `ID` string where REST has the DB-native
	// integer. The field sets match — the migration's equivalence run proved it —
	// so this is the representation-only cast of
	// rest-migration/feature-flags.md rule 4.
	type GqlShapedArtwork = GetPrimaryGalleryArtworksQuery['Primary_Artworks'][0];

	const dataCy = 'primary-gallery';
	const { dropTypes } = getCartIconContext();
	const wishlistCtx = getWishlistContext();

	// While the flag is off the page data and the load-more query are the
	// GraphQL ones; each branch reads the page data through its own shape
	// (rest-migration/feature-flags.md).
	const useRest =
		FeatureFlags.RestPages['/primary/collection/[collectionSlug]'];

	let data = $derived(page.data as PrimaryGalleryPageData);
	let slug = $derived(page.params.collectionSlug);

	// `PrimaryGalleryPageData` is the union of both loaders' returns, so each
	// path's own key is optional on it. The REST branch only runs when the flag
	// is on, which is exactly when the REST universal load supplied `params`.
	let restParams = $derived(
		data.params as NonNullable<PrimaryGalleryPageData['params']>
	);

	let filterSections = $derived(data.filterSections);

	let collection = $derived(data.collection);

	let crumbs = $derived([
		{ label: 'Home', href: GlobalRoutes.Home },
		{ label: 'Primary', href: GlobalRoutes.PrimaryHome },
		{ label: collection.name || '' },
	]);

	let isReady = $state(false);

	onMount(() => {
		isReady = true;
	});

	let restQuery = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: primaryCollectionArtworksQueryKey(restParams),
					queryFn: (offset) =>
						getPrimaryCollectionArtworks(undefined, {
							...restParams,
							offset,
						}),
					limit: PRIMARY_GALLERY_LIMIT,
				})
			: undefined
	);

	let gqlQuery = $derived(
		useRest
			? undefined
			: infiniteQuery({
					gqlClient,
					variables: data.variables,
					document: GetPrimaryGalleryArtworksDocument,
					limit: PRIMARY_GALLERY_LIMIT,
				})
	);

	let artworks = $derived(
		(useRest
			? getInfiniteData({
					query: restQuery!,
					transform: (page) => page.artworks,
				})
			: getInfiniteData({
					query: gqlQuery!,
					transform: (page) => page.Primary_Artworks,
				})) as unknown as GqlShapedArtwork[]
	);

	let dropPageSlug = $derived(data.dropPageSlug);
	// REST flattens `total` to a plain number; GraphQL wraps it in an
	// array-of-one around `countDistinct`.
	let count = $derived(
		useRest
			? restQuery?.data?.pages[0]?.total || 0
			: gqlQuery?.data?.pages[0].total[0]?.countDistinct?.id || 0
	);
	let hasMore = $derived(count > (artworks?.length || 0));
	let isFetching = $derived(
		useRest ? !!restQuery?.isFetching : !!gqlQuery?.isFetching
	);
	let loadingNextPage = $derived(
		useRest ? !!restQuery?.isFetchingNextPage : !!gqlQuery?.isFetchingNextPage
	);
	let loading = $derived((isFetching && !loadingNextPage) || !isReady);

	let sortOptions = $derived(data.sortOptions);

	let isCartEnabled = $derived(
		$dropTypes?.[dropPageSlug]?.type === DropType.Purchase &&
			artworks?.[0] &&
			!artworks?.[0]?.product?.primary_generic_artwork?.[0]?.id
	);

	// The wishlist FAB shows on active application drops only.
	let isWishlistEnabled = $derived(
		FeatureFlags.Wishlist &&
			!!wishlistCtx &&
			$dropTypes?.[dropPageSlug]?.type === DropType.Application
	);

	const handleLoadMore = () => {
		if (useRest) {
			restQuery?.fetchNextPage();
			return;
		}
		gqlQuery?.fetchNextPage();
	};
</script>

<PageBody>
	<Breadcrumbs {dataCy} {crumbs} />

	<div class="mt-10 md:mt-9">
		<StickyFilterToolbar
			{dataCy}
			{filterSections}
			sorts={[sortOptions]}
			{count}
			{loading}
			withMaxSectionHeight={false}
			trackPrefix="PRIMARY"
			resultsClass="mb-8 md:mb-6"
		>
			{#snippet title()}
				<Txt variant="h3" component="h1">Artworks</Txt>
			{/snippet}
		</StickyFilterToolbar>

		<Container {dataCy}>
			{#if loading}
				<div
					class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
				>
					{#each Array(12).fill(null)}
						<ArtworkCardLoading lines={3} {dataCy} />
					{/each}
				</div>
			{:else if artworks && artworks.length > 0}
				<div
					class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
				>
					{#each artworks as artwork, index}
						{#if slug}
							<div class="relative">
								<PrimaryGalleryArtworkCard
									{artwork}
									{slug}
									{dataCy}
									onclick={() =>
										trackSelectItem({
											itemId:
												artwork.artwork_code || artwork.slug || artwork.id,
											position: index,
											listId: 'primary_gallery',
											section: slug,
											artist: artwork.artists
												?.map((artist) => artist?.Artists_id?.name)
												?.join(', '),
										})}
								/>
								{#if isCartEnabled && !artwork?.product?.in_stock}
									<span
										data-cy={`${dataCy}-unavailable`}
										class={'absolute top-2 right-2 inline-block rounded-lg bg-gray-900 p-1 px-2'}
									>
										<Txt variant="label4" class="text-white">UNAVAILABLE</Txt>
									</span>
								{/if}
								{#if isWishlistEnabled}
									<WishlistButton
										productId={artwork.product?.id}
										artist={artwork.artists
											?.map((artist) => artist?.Artists_id?.name)
											?.join(', ')}
										trackPrefix="PRIMARY"
										cartItem={{
											entityId: artwork.id,
											restricted: false,
											sku: artwork.product?.sku,
											group: artwork.primary_collection?.name,
											groupId: artwork.primary_collection?.code,
											currency: artwork.product?.currency,
											price: artwork.product?.price,
											name: artwork.artwork_title,
											dropConfigId:
												artwork.primary_collection?.drop_page?.drop_config?.[0]
													?.id,
										}}
										dataCy={`${dataCy}-${artwork.slug}`}
										class="bg-gray-0/80 hover:bg-gray-0 absolute top-2 right-2 h-10 w-10"
									/>
								{/if}
							</div>
						{/if}
					{/each}
				</div>

				<LoadMoreInfinite
					{dataCy}
					{hasMore}
					onLoadMore={handleLoadMore}
					initialLoadMoreButton={FeatureFlags.loadButtonBeforeInfiniteScroll}
					trackPrefix="PRIMARY"
					loadedCount={artworks?.length || 0}
				/>

				{#if loadingNextPage}
					<div class="flex justify-center">
						<CircularProgress {dataCy} />
					</div>
				{/if}
			{:else}
				<div class="flex min-h-[50vh] flex-col justify-center p-2">
					<Txt variant="h2" class="mb-4 text-center">No artworks found</Txt>

					<Txt class="text-center"
						>Please try a different search or widen the filters.</Txt
					>
				</div>
			{/if}
		</Container>
	</div>
</PageBody>
